// The languages the public Signpost sites can show, copied from signpost-monorepo lib/locale.ts.
// The sites look text up by these exact keys, so content in any other code (e.g. "ar" rather than "ar-SA")
// is never shown. Keep this in step with that file: it's the only list the Smart CMS lets anyone add from.

export interface SiteLanguage {
  name: string // in the language itself, what readers see
  english: string
  rtl?: boolean
  zendesk?: string // URL alias the sites also accept
}

export const SITE_LANGUAGES: Record<string, SiteLanguage> = {
  "ar-SA": { rtl: true, name: "العربية", english: "Arabic", zendesk: "ar" },
  bn: { name: "বাংলা", english: "Bengali" },
  my: { name: "ဗမာ", english: "Burmese" },
  cs: { name: "Čeština", english: "Czech", zendesk: "cs" },
  "fa-AF": { rtl: true, name: "دری", english: "Dari" },
  "en-US": { name: "English", english: "English" },
  "fa-FA": { rtl: true, name: "فارسی", english: "Persian", zendesk: "fa" },
  "fr-FR": { name: "Français", english: "French", zendesk: "fr" },
  "de-DE": { name: "Deutsch", english: "German", zendesk: "de" },
  el: { name: "Ελληνικά", english: "Greek", zendesk: "el-gr" },
  ht: { name: "Kreyòl", english: "Haitian Creole", zendesk: "ht" },
  "fr-HT": { name: "Français haïtien", english: "French (Haiti)", zendesk: "fr-ht" },
  "ha-HA": { name: "Hausa", english: "Hausa" },
  "hu-HU": { name: "Magyar", english: "Hungarian" },
  "it-IT": { name: "Italiano", english: "Italian", zendesk: "it" },
  KAU: { name: "Kanuri", english: "Kanuri", zendesk: "ko-kr" },
  "ki-KI": { name: "Kirundi", english: "Kirundi", zendesk: "rn-BI" },
  "ln-LN": { name: "Lingála", english: "Lingala", zendesk: "ln" },
  "ps-PS": { rtl: true, name: "پښتو", english: "Pashto (Afghanistan)", zendesk: "ps" },
  "ru-RU": { name: "Русский", english: "Russian", zendesk: "ru" },
  "so-SO": { name: "Soomaali", english: "Somali", zendesk: "so" },
  "es-ES": { name: "Español", english: "Spanish", zendesk: "es" },
  "sw-SW": { name: "Kiswahili", english: "Swahili", zendesk: "sw" },
  th: { name: "ไทย", english: "Thai", zendesk: "th" },
  "uk-UK": { name: "Українська", english: "Ukrainian", zendesk: "uk" },
  "ur-UR": { rtl: true, name: "اردو", english: "Urdu", zendesk: "ur" },
  "vi-VN": { name: "Tiếng Việt", english: "Vietnamese", zendesk: "vi" },
  "ko-KR": { name: "한국어", english: "Korean", zendesk: "ko" },
  "zh-CN": { name: "中文", english: "Chinese", zendesk: "zh-cn" },
  am: { name: "አማርኛ", english: "Amharic", zendesk: "am" },
}

export const SITE_LOCALE_CODES = Object.keys(SITE_LANGUAGES)

// The supported key for a typed code, matching case and the sites' URL aliases ("ar" → "ar-SA"), or undefined
export function siteLocaleFor(input: string): string | undefined {
  const lower = input.trim().replace(/_/g, "-").toLowerCase()
  if (!lower) return undefined
  return SITE_LOCALE_CODES.find(c => c.toLowerCase() === lower)
    ?? SITE_LOCALE_CODES.find(c => SITE_LANGUAGES[c].zendesk?.toLowerCase() === lower)
}

export const isSiteLocale = (code: string | null | undefined): boolean => !!code && code in SITE_LANGUAGES

// Why a code can't be used, pointing at the supported one for the same language when there is one
export function unsupportedLocaleMessage(code: string): string {
  const same = siteLocaleFor(code)
  return same && same !== code
    ? `"${code}" isn't a code the Signpost sites use for ${SITE_LANGUAGES[same].english}: use ${same}.`
    : `"${code}" isn't a language the Signpost sites can show. Pick one from the supported list.`
}
